import { type Ref, type Reactive } from "vue";
import {
  OperationInput,
  ScheduledDeliveryCreateInput,
  ScheduledDeliveryUpdateInput,
  ShopListInput,
  GetShopQuery,
  GetShopDocument,
  DeliverableAreaDeliveryIdInput,
  ListDeliverableAreaDeliveryIdQuery,
  ListDeliverableAreaDeliveryIdDocument,
  CreateScheduledDeliveryMutation,
  CreateScheduledDeliveryMutationVariables,
  CreateScheduledDeliveryDocument,
  ListDeliveryTimeQuery,
  ListDeliveryTimeQueryVariables,
  ListDeliveryTimeDocument,
  DeliveryTimeListInput,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  DeliveryAreaSectionListInput,
  DeliveryAreaSectionCountInput,
  ListDeliveryAreaDocument,
  ListDeliveryAreaSectionAssignedQuery,
  ListDeliveryAreaSectionAssignedDocument,
  GetScheduledDeliveryQuery,
  GetScheduledDeliveryQueryVariables,
  GetScheduledDeliveryDocument,
  UpdateScheduledDeliveryMutation,
  UpdateScheduledDeliveryMutationVariables,
  UpdateScheduledDeliveryDocument,
  DeleteScheduledDeliveryMutation,
  DeleteScheduledDeliveryMutationVariables,
  DeleteScheduledDeliveryDocument,
} from "@/generated/graphql";
import { localStorageGetCustomerCode } from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { ApolloQueryResult, FetchResult } from "@apollo/client/core";
import type {
  ListData,
  ShopDataType,
  DeliveryAreaType,
  DeliveryTimeResponseType,
} from "../types";

/**
 * 店舗間定期配達登録 API通信
 * GraphQL通信の一元管理、エラーハンドリング
 */
export const useScheduledDeliveryRegisterApi = (deps: {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  scheduledDeliveryCreateInput: Reactive<ScheduledDeliveryCreateInput>;
  scheduledDeliveryUpdateInput: Reactive<ScheduledDeliveryUpdateInput>;
  operationInput: Reactive<OperationInput>;
  deliveryTimeListInput: Reactive<DeliveryTimeListInput>;
  deliverableAreaDeliveryIdInput: Reactive<DeliverableAreaDeliveryIdInput>;
  areaListItems: Ref<DeliveryAreaType[] | null | undefined>;
  listDeliveryTime: Ref<DeliveryTimeResponseType[] | null | undefined>;
  listDeliveryId: Ref<ListData[]>;
}) => {
  const {
    errdialog,
    errMessage,
    scheduledDeliveryCreateInput,
    scheduledDeliveryUpdateInput,
    operationInput,
    deliveryTimeListInput,
    deliverableAreaDeliveryIdInput,
    areaListItems,
    listDeliveryTime,
    listDeliveryId,
  } = deps;

  /**
   * 店舗情報取得
   */
  async function getShopData(
    shop_code: string
  ): Promise<ApolloQueryResult<GetShopQuery> | undefined> {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      const shop_data: ApolloQueryResult<GetShopQuery> =
        await apolloClient.query<GetShopQuery>({
          query: GetShopDocument,
          variables: {
            chain_code: scheduledDeliveryCreateInput.chain_code,
            customer_code: localStorageGetCustomerCode(),
            shop_code: shop_code,
          },
        });

      return shop_data;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return undefined;
    }
  }

  /**
   * エリア情報取得
   */
  async function GetAreaData(shop_code: string, zip_code: string) {
    if (shop_code == "" || zip_code == "") {
      return;
    }

    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      // エリアデータ取得処理
      const listAreaRes: ApolloQueryResult<ListDeliveryAreaQuery> =
        await apolloClient.query<
          ListDeliveryAreaQuery,
          ListDeliveryAreaQueryVariables
        >({
          query: ListDeliveryAreaDocument,
          variables: {
            chain_code: scheduledDeliveryCreateInput.chain_code,
            customer_code: localStorageGetCustomerCode(),
            shop_code: shop_code,
          },
          fetchPolicy: "no-cache",
        });

      // 配達区画登録済一覧 input [limit 取得件数 offset:取得開始位置]
      const listInput: DeliveryAreaSectionListInput = {
        chain_code: scheduledDeliveryCreateInput.chain_code,
        customer_code: localStorageGetCustomerCode(),
        shop_code: shop_code,
        zip_code: zip_code,
        limit: 9999,
        offset: 0,
      };

      // 配達区画未登録件数 input
      const countInput: DeliveryAreaSectionCountInput = {
        chain_code: listInput.chain_code,
        customer_code: listInput.customer_code,
        shop_code: listInput.shop_code,
        zip_code: listInput.zip_code,
      };

      // 未登録配達区画一覧取得
      let listDeliveryAreaSectionAssigned: string[] = [];
      let result: ApolloQueryResult<ListDeliveryAreaSectionAssignedQuery>;
      try {
        //graphql接続
        const apolloClient2 = await generateApolloClient();

        result =
          await apolloClient2.query<ListDeliveryAreaSectionAssignedQuery>({
            query: ListDeliveryAreaSectionAssignedDocument,
            variables: { input: listInput, countInput: countInput },
            fetchPolicy: "no-cache",
          });
        if (
          result.data.listDeliveryAreaSectionAssigned === undefined ||
          result.data.listDeliveryAreaSectionAssigned === null
        ) {
          // 登録配達区画一覧取得 エラー
          console.error(
            "result.data.listDeliveryAreaSectionAssigned is undefined or null"
          );
          return;
        }
        if (
          result.data.getDeliveryAreaSectionAssignedCount === undefined ||
          result.data.getDeliveryAreaSectionAssignedCount === null
        ) {
          // 登録配達区画件数 エラー
          console.error(
            "result.data.listDeliveryAreaSectionAssigned is undefined or null"
          );
          return;
        }

        if (
          result.data.listDeliveryAreaSectionAssigned !== null &&
          result.data.listDeliveryAreaSectionAssigned != undefined
        ) {
          result.data.listDeliveryAreaSectionAssigned.forEach((value) => {
            if (
              value !== null &&
              value !== undefined &&
              value.area_code &&
              (listDeliveryAreaSectionAssigned.length == 0 ||
                listDeliveryAreaSectionAssigned.indexOf(value.area_code) == -1)
            ) {
              listDeliveryAreaSectionAssigned.push(value.area_code);
            }
          });
        }
      } catch (err: unknown) {
        console.error(err);
        errdialog.value = true;
        if (err instanceof Error) {
          errMessage.value = err.message;
        } else {
          errMessage.value = "不明なエラーが発生しました。";
        }
        return;
      }

      if (
        listAreaRes.data.listDeliveryArea !== null &&
        listAreaRes.data.listDeliveryArea != undefined
      ) {
        listAreaRes.data.listDeliveryArea.forEach((value) => {
          if (
            value !== undefined &&
            value !== null &&
            areaListItems.value !== null &&
            areaListItems.value !== undefined &&
            listDeliveryAreaSectionAssigned.indexOf(value.area_code) !== -1
          ) {
            (areaListItems.value as DeliveryAreaType[]).push(value);
            (deliveryTimeListInput.area_code as string[]).push(
              value.area_code
            );
          }
        });
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = "配達エリアが存在しません。";
      return;
    }
  }

  /**
   * 配達時間一覧処理
   */
  async function queryDeliverytime() {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 配達時間一覧取得 [引数:input参照]
    let deliveryTime: ApolloQueryResult<ListDeliveryTimeQuery>;
    try {
      deliveryTime = await apolloClient.query<
        ListDeliveryTimeQuery,
        ListDeliveryTimeQueryVariables
      >({
        query: ListDeliveryTimeDocument,
        variables: {
          input: deliveryTimeListInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err) {
      console.error(err);
      return;
    }

    if (deliveryTime.data.listDeliveryTime!.length > 0) {
      listDeliveryTime.value = deliveryTime.data.listDeliveryTime!;
      listDeliveryId.value = [];

      const addedIds = new Set<string>();
      const regularAreaCodes = new Set<string>();

      listDeliveryTime.value!.forEach((value) => {
        if (value !== undefined && value !== null) {
          areaListItems.value!.forEach((area) => {
            if (
              area !== undefined &&
              area !== null &&
              area.area_code === value.area_code
            ) {
              regularAreaCodes.add(area.area_code);
            }
          });
        }
      });

      areaListItems.value!.forEach((area) => {
        if (
          area !== undefined &&
          area !== null &&
          regularAreaCodes.has(area.area_code)
        ) {
          const areaDeliveryId = area.area_code + "99";
          if (!addedIds.has(areaDeliveryId)) {
            addedIds.add(areaDeliveryId);
            listDeliveryId.value.push({
              area_delivery_id: areaDeliveryId,
              area_code: area.area_code,
              delivery_id: 99,
              delivery_name: area.area_name + "　定期便",
              delivery_start_time: "",
              delivery_end_time: "",
              delivery_time: "",
            });
          }
        }
      });

      let area_name = "";
      listDeliveryTime.value!.forEach((value) => {
        if (value !== undefined && value !== null) {
          if (value.delivery_id !== 0) {
            // 都度便以外
            area_name = "";
            areaListItems.value!.forEach((area) => {
              if (
                area !== undefined &&
                area !== null &&
                area.area_code === value.area_code
              ) {
                area_name = area.area_name;
              }
            });
            const areaDeliveryId = value.area_code + String(value.delivery_id);
            if (!addedIds.has(areaDeliveryId)) {
              addedIds.add(areaDeliveryId);
              listDeliveryId.value.push({
                area_delivery_id: areaDeliveryId,
                area_code: value.area_code,
                delivery_id: value.delivery_id,
                delivery_name:
                  area_name + "　" + String(value.delivery_id) + "便",
                delivery_start_time:
                  value.delivery_start_time?.substring(0, 5) ?? "",
                delivery_end_time:
                  value.delivery_end_time?.substring(0, 5) ?? "",
                delivery_time:
                  (value.delivery_start_time?.substring(0, 5) ?? "") +
                  "~" +
                  (value.delivery_end_time?.substring(0, 5) ?? ""),
              });
            }
          }
        }
      });
    }
  }

  /**
   * 配達便存在チェック
   */
  async function chkDeliveryId() {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      deliverableAreaDeliveryIdInput.customer_code =
        scheduledDeliveryCreateInput!.customer_code;
      deliverableAreaDeliveryIdInput.chain_code =
        scheduledDeliveryCreateInput.chain_code;
      deliverableAreaDeliveryIdInput.shop_code =
        scheduledDeliveryCreateInput.shop_code;

      // 配達便が存在しているかチェック
      const deliverableAreaDeliveryIdRes: ApolloQueryResult<ListDeliverableAreaDeliveryIdQuery> =
        await apolloClient.query<ListDeliverableAreaDeliveryIdQuery>({
          query: ListDeliverableAreaDeliveryIdDocument,
          variables: {
            input: deliverableAreaDeliveryIdInput,
          },
        });

      //存在していない場合エラー
      deliverableAreaDeliveryIdRes.data.listDeliverableAreaDeliveryId?.forEach(
        (value) => {
          if (value !== undefined && value !== null) {
            (deliveryTimeListInput.area_code as string[]).push(
              value.area_code
            );
          }
        }
      );
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "予期せぬエラーが発生しました。";
      }
      return false;
    }
  }

  /**
   * 店舗間定期配達情報取得
   */
  async function getScheduledDelivery(
    scheduled_delivery_no: string
  ): Promise<void | undefined> {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      // 店舗間定期配達一覧作成 logInput
      operationInput.detail = "店舗間定期配達一覧";
      operationInput.display_category = "店舗間定期配達一覧表示";

      let result: ApolloQueryResult<GetScheduledDeliveryQuery>;
      result = await apolloClient.query<
        GetScheduledDeliveryQuery,
        GetScheduledDeliveryQueryVariables
      >({
        query: GetScheduledDeliveryDocument,
        variables: {
          scheduled_delivery_no: scheduled_delivery_no,
          logInput: operationInput,
        },
        fetchPolicy: "no-cache",
      });

      if (
        result.data.getScheduledDelivery !== undefined &&
        result.data.getScheduledDelivery !== null
      ) {
        scheduledDeliveryCreateInput.area_code =
          result.data.getScheduledDelivery.area_code!;
        scheduledDeliveryCreateInput.delivery_id =
          result.data.getScheduledDelivery.delivery_id;
        scheduledDeliveryCreateInput.irregular_delivery_start_time =
          result.data.getScheduledDelivery.irregular_delivery_start_time.substring(
            0,
            5
          );
        scheduledDeliveryCreateInput.irregular_delivery_end_time =
          result.data.getScheduledDelivery.irregular_delivery_end_time.substring(
            0,
            5
          );
        scheduledDeliveryCreateInput.delivery_request_create_date =
          result.data.getScheduledDelivery.delivery_request_create_date;
        scheduledDeliveryCreateInput.delivery_request_create_time =
          result.data.getScheduledDelivery.delivery_request_create_time;
        scheduledDeliveryCreateInput.mon_to_deliver =
          result.data.getScheduledDelivery.mon_to_deliver;
        scheduledDeliveryCreateInput.tue_to_deliver =
          result.data.getScheduledDelivery.tue_to_deliver;
        scheduledDeliveryCreateInput.wed_to_deliver =
          result.data.getScheduledDelivery.wed_to_deliver;
        scheduledDeliveryCreateInput.thu_to_deliver =
          result.data.getScheduledDelivery.thu_to_deliver;
        scheduledDeliveryCreateInput.fri_to_deliver =
          result.data.getScheduledDelivery.fri_to_deliver;
        scheduledDeliveryCreateInput.sat_to_deliver =
          result.data.getScheduledDelivery.sat_to_deliver;
        scheduledDeliveryCreateInput.sun_to_deliver =
          result.data.getScheduledDelivery.sun_to_deliver;
        scheduledDeliveryCreateInput.valid_date_from =
          result.data.getScheduledDelivery.valid_date_from;
        scheduledDeliveryCreateInput.valid_date_to =
          result.data.getScheduledDelivery.valid_date_to;
        scheduledDeliveryCreateInput.delivery_notes =
          result.data.getScheduledDelivery.delivery_notes;
      }

      scheduledDeliveryUpdateInput.scheduled_delivery_no =
        scheduled_delivery_no;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return undefined;
    }
  }

  /**
   * 店舗間定期配達登録作成
   */
  async function createScheduledDelivery(): Promise<boolean> {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      // 店舗間定期配達作成 logInput
      operationInput.detail = "店舗間定期配達登録";
      operationInput.display_category = "店舗間定期配達登録";

      const result: FetchResult<CreateScheduledDeliveryMutation> =
        await apolloClient.mutate<
          CreateScheduledDeliveryMutation,
          CreateScheduledDeliveryMutationVariables
        >({
          mutation: CreateScheduledDeliveryDocument,
          variables: {
            input: scheduledDeliveryCreateInput,
            logInput: operationInput,
          },
        });
      const createScheduledDeliveryMutation = result.data;

      if (
        createScheduledDeliveryMutation === undefined ||
        createScheduledDeliveryMutation === null ||
        !createScheduledDeliveryMutation.createScheduledDelivery?.Result
      ) {
        errdialog.value = true;
        errMessage.value = "店舗間定期配達の登録に失敗しました。";
        return false;
      }

      return true;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = "店舗間定期配達の登録に失敗しました。";
      } else {
        errMessage.value = "予期せぬエラーが発生しました。";
      }
      return false;
    }
  }

  /**
   * 店舗間定期配変更
   */
  async function updateScheduledDelivery(): Promise<boolean> {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      // 店舗間定期配達作成 logInput
      operationInput.detail = "店舗間定期配達変更";
      operationInput.display_category = "店舗間定期配達変更";

      const result: FetchResult<UpdateScheduledDeliveryMutation> =
        await apolloClient.mutate<
          UpdateScheduledDeliveryMutation,
          UpdateScheduledDeliveryMutationVariables
        >({
          mutation: UpdateScheduledDeliveryDocument,
          variables: {
            input: scheduledDeliveryUpdateInput,
            logInput: operationInput,
          },
        });
      const updateScheduledDeliveryMutation = result.data;

      if (
        updateScheduledDeliveryMutation === undefined ||
        updateScheduledDeliveryMutation === null ||
        !updateScheduledDeliveryMutation.updateScheduledDelivery?.Result
      ) {
        errdialog.value = true;
        errMessage.value = "店舗間定期配達の変更に失敗しました。";
        return false;
      }

      return true;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = "店舗間定期配達の変更に失敗しました。";
      } else {
        errMessage.value = "予期せぬエラーが発生しました。";
      }
      return false;
    }
  }

  /**
   * 店舗間定期配削除
   */
  async function deleteScheduledDelivery(): Promise<boolean> {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      // 店舗間定期配達作成 logInput
      operationInput.detail = "店舗間定期配達削除";
      operationInput.display_category = "店舗間定期配達削除";

      const result: FetchResult<DeleteScheduledDeliveryMutation> =
        await apolloClient.mutate<
          DeleteScheduledDeliveryMutation,
          DeleteScheduledDeliveryMutationVariables
        >({
          mutation: DeleteScheduledDeliveryDocument,
          variables: {
            scheduled_delivery_no:
              scheduledDeliveryUpdateInput.scheduled_delivery_no,
            logInput: operationInput,
          },
        });
      const deleteScheduledDeliveryMutation = result.data;

      if (
        deleteScheduledDeliveryMutation === undefined ||
        deleteScheduledDeliveryMutation === null ||
        !deleteScheduledDeliveryMutation.deleteScheduledDelivery?.Result
      ) {
        errdialog.value = true;
        errMessage.value = "店舗間定期配達の削除に失敗しました。";
        return false;
      }

      return true;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = "店舗間定期配達の削除に失敗しました。";
      } else {
        errMessage.value = "予期せぬエラーが発生しました。";
      }
      return false;
    }
  }

  return {
    getShopData,
    GetAreaData,
    queryDeliverytime,
    chkDeliveryId,
    getScheduledDelivery,
    createScheduledDelivery,
    updateScheduledDelivery,
    deleteScheduledDelivery,
  };
};
